import { useEffect } from "react";
import type { Pillar } from "./pillarsData";

export default function PillarModal({
  pillar,
  onClose,
}: {
  pillar: Pillar | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!pillar) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pillar, onClose]);

  useEffect(() => {
    if (pillar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [pillar]);

  if (!pillar) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={pillar.title}
    >
      <div
        className="absolute inset-0 bg-bg/90 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative w-full h-full overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-24 pb-12 md:pt-32 md:pb-20">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl">
              <span className="text-xs tracking-[0.4em] uppercase text-moss mb-4 block">
                {pillar.num} · {pillar.title.toUpperCase()}
              </span>
              <h2 className="font-display text-4xl md:text-6xl font-light leading-tight text-ink mb-6">
                {pillar.title.split(" ")[0]}{" "}
                <em className="italic text-sage">{pillar.title.split(" ").slice(1).join(" ")}</em>
              </h2>
              <p className="text-inkDim text-sm md:text-base leading-relaxed">
                {pillar.desc}
              </p>
            </div>

            <button
              onClick={onClose}
              className="self-start md:self-start w-11 h-11 flex items-center justify-center rounded-full border border-ink/20 hover:bg-ink hover:text-bg text-ink transition-all cursor-pointer shrink-0"
              aria-label="Schließen"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Cases */}
          <div>
            {pillar.cases.map((c, i) => (
              <div
                key={c.title}
                className="grid grid-cols-1 md:grid-cols-[72px_1fr] gap-4 md:gap-8 py-10 md:py-12 border-t border-line"
              >
                <div className="font-display text-sm text-moss tracking-[0.2em] pt-2">
                  {String(i + 1).padStart(2, "0")}
                </div>

                <div>
                  <h4 className="font-display text-2xl md:text-3xl text-ink font-light mb-5 leading-snug">
                    {c.title}
                  </h4>

                  {c.paragraphs?.map((p) => {
                    if (c.link && p.includes(c.link.word)) {
                      const parts = p.split(c.link.word);
                      return (
                        <p
                          key={p}
                          className="text-inkDim text-sm md:text-base leading-relaxed mb-3 max-w-2xl"
                        >
                          {parts.map((part, idx) => (
                            <span key={idx}>
                              {part}
                              {idx < parts.length - 1 && (
                                <a
                                  href={c.link!.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-moss underline decoration-moss/40 underline-offset-4 hover:text-ink transition-colors"
                                >
                                  {c.link!.word}
                                </a>
                              )}
                            </span>
                          ))}
                        </p>
                      );
                    }
                    return (
                      <p
                        key={p}
                        className="text-inkDim text-sm md:text-base leading-relaxed mb-3 max-w-2xl"
                      >
                        {p}
                      </p>
                    );
                  })}

                  {c.list && (
                    <ul className="my-6 space-y-2">
                      {c.list.map((li) => {
                        const text = typeof li === "string" ? li : li.text;
                        const url = typeof li === "string" ? null : li.url;
                        return (
                          <li
                            key={text}
                            className="flex items-center gap-3 text-sm md:text-base text-ink"
                          >
                            <span className="w-6 h-6 flex items-center justify-center rounded-full border border-moss/40 text-moss shrink-0">
                              <i className="ri-arrow-right-line text-xs"></i>
                            </span>
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-moss underline decoration-moss/40 underline-offset-4 hover:text-ink transition-colors"
                              >
                                {text}
                              </a>
                            ) : (
                              text
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {c.punch && (
                    <div className="mt-6 space-y-2">
                      {c.punch.map((p) => (
                        <p
                          key={p}
                          className="font-display italic text-lg md:text-xl text-sage leading-relaxed"
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}